import type { FC, ReactNode } from 'react'
import type { OpeningStatus, OpeningTone } from '@/lib/opening-hours'

import { cn } from '@/lib/utils'
import { useOpeningStatus } from '@/hooks/use-opening-status'

const DOT_TONES: Record<OpeningTone, string> = {
    open: 'bg-success',
    soon: 'bg-warning',
    closed: 'bg-muted-foreground'
}

// Point d'état statique : pas de halo, pas de pulsation.
export const StatusDot: FC<{ status: OpeningStatus | null, className?: string }> = ({ status, className }): ReactNode => {
    return (
        <span
            aria-hidden='true'
            className={cn('size-2 shrink-0 rounded-full', status ? DOT_TONES[status.tone] : 'bg-border', className)}
        />
    )
}

interface OpeningStatusChipProps {
    className?: string
}

// Pastille de l'en-tête : deux lignes compactes sur mobile, une ligne dès 640 px.
export const OpeningStatusChip: FC<OpeningStatusChipProps> = ({ className }): ReactNode => {
    const status = useOpeningStatus()

    return (
        <a
            href='#contact'
            className={cn(
                'inline-flex h-12 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card/70 px-2.5 transition-colors duration-150 hover:border-foreground/40 sm:h-11 sm:gap-2 sm:px-4',
                className
            )}
        >
            <StatusDot status={status} />

            {status ? (
                <span className='flex flex-col leading-tight sm:flex-row sm:items-baseline sm:gap-1.5'>
                    <span className='text-[0.8125rem] font-semibold text-foreground sm:hidden'>
                        {status.short}
                    </span>

                    <span className='hidden text-sm font-semibold text-foreground sm:inline'>
                        {status.state}
                    </span>

                    <span className='text-[0.6875rem] text-muted-foreground sm:hidden'>
                        {status.compact}
                    </span>

                    <span className='hidden text-sm text-muted-foreground sm:inline'>
                        · {status.detail}
                    </span>
                </span>
            ) : (
                <span className='flex flex-col leading-tight sm:flex-row sm:items-baseline sm:gap-1.5'>
                    <span className='text-[0.8125rem] font-semibold text-foreground sm:text-sm'>
                        Horaires
                    </span>

                    <span className='text-[0.6875rem] text-muted-foreground sm:text-sm'>
                        lun. au sam.
                    </span>
                </span>
            )}

            <span className='sr-only'>
                , voir les horaires détaillés
            </span>
        </a>
    )
}

interface OpeningStatusLineProps {
    className?: string
}

// Version texte pour la barre mobile et la section contact.
export const OpeningStatusLine: FC<OpeningStatusLineProps> = ({ className }): ReactNode => {
    const status = useOpeningStatus()

    return (
        <p className={cn('flex items-center gap-2 text-sm', className)}>
            <StatusDot status={status} />

            {status ? (
                <span>
                    <span className='font-semibold text-foreground'>
                        {status.state}
                    </span>

                    <span className='text-muted-foreground'>
                        {' '}· {status.detail}
                    </span>
                </span>
            ) : (
                <span className='text-muted-foreground'>
                    Du lundi au samedi
                </span>
            )}
        </p>
    )
}
